import * as React from 'react';
import { FieldProps } from '../metadataFormField';
import { getFieldValue, getItemStatusId } from '../../../utils';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import './configureWorkflowField.scss';
import { useSelector } from '../../../utils/useSelector';
import { ItemStatus } from '../../../enums/ItemStatus';
import { ConfigureWorkflowContent } from './configureWorkflowContent';
import { useActions } from '../metadataFormActions';
import { Metadata } from '../../../types/Metadata';
import { WorkflowSavePayload } from '../../../types/ConfigureWorkflowTypes';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';

export const ConfigureWorkflowField = (props: FieldProps) => {
  const { updateConfigureWorkflow, setShowConfigureWorkflowModal } =
    useActions();
  const empCd = useSelector(s => s.app.user.employeeId);

  const schema = useSelector(s => s.form?.schema);
  const showConfigureWorkflowModal = useSelector(
    s => s.form?.showConfigureWorkflowModal
  );

  const [isLightUpdateModal, setIsLightUpdateModal] = React.useState(false);
  const { field, data, disabled } = props;
  const itemStatusId = getItemStatusId(data);
  const value = getFieldValue(field, data);
  const enumValues = field?.enumValues || {};

  // Get the light update workflow field and its data
  const lightUpdateField = React.useMemo(() => {
    return schema?.props?.fields?.find(
      (f: any) => f.field === 'configureLightUpdateWorkflowType'
    );
  }, [schema]);

  const lightUpdateValue = React.useMemo(() => {
    return lightUpdateField ? getFieldValue(lightUpdateField, data) : null;
  }, [lightUpdateField, data]);

  const lightUpdateEnumValues = lightUpdateField?.enumValues || {};
  const [initialWorkflowState, setInitialWorkflowState] =
    React.useState<WorkflowSavePayload>(null);

  const closeModal = React.useCallback(() => {
    setIsLightUpdateModal(false);
    setShowConfigureWorkflowModal(false);
  }, [setShowConfigureWorkflowModal]);

  React.useEffect(() => {
    if (showConfigureWorkflowModal) {
      setInitialWorkflowState({
        configureWorkflowSkipIds: data?.props?.configureWorkflowSkipIds,
        configureWorkflowType: data?.props?.configureWorkflowType,
        requestCreativeHelp: data?.props?.requestCreativeHelp || false,
        preProductionExternalReview:
          data.props?.preProductionExternalReview ?? false,
        postProductionExternalReview:
          data.props?.postProductionExternalReview ?? false
      });
    }
  }, [showConfigureWorkflowModal, data]);

  const { onShowModal, onShowLightUpdateModal } = React.useMemo(
    () => ({
      onShowModal: () => {
        setIsLightUpdateModal(false);
        setInitialWorkflowState({
          configureWorkflowSkipIds: data.props?.configureWorkflowSkipIds,
          configureWorkflowType: data.props?.configureWorkflowType,
          requestCreativeHelp: data.props?.requestCreativeHelp || false,
          preProductionExternalReview:
            data.props?.preProductionExternalReview ?? false,
          postProductionExternalReview:
            data.props?.postProductionExternalReview ?? false
        });
        setShowConfigureWorkflowModal(true);
      },
      onShowLightUpdateModal: () => {
        setIsLightUpdateModal(true);
        setInitialWorkflowState({
          configureWorkflowSkipIds:
            data.props?.configureLightUpdateWorkflowSkipIds,
          configureWorkflowType: data.props?.configureLightUpdateWorkflowType,
          requestCreativeHelp: data.props?.requestCreativeHelp || false
        });
        setShowConfigureWorkflowModal(true);
      }
    }),
    [data]
  );

  const handleMainWorkflowSave = React.useCallback(
    async ({
      requestCreativeHelp,
      configureWorkflowType,
      configureWorkflowSkipIds,
      preProductionExternalReview,
      postProductionExternalReview
    }: WorkflowSavePayload) => {
      const newData: Partial<Metadata> = {
        id: data.id,
        labels: data.labels,
        props: {
          refId: data.props.refId,
          requestCreativeHelp,
          configureWorkflowByEmpCd: empCd,
          configureWorkflowType,
          configureWorkflowSkipIds,
          preProductionExternalReview,
          postProductionExternalReview
        }
      };
      if (!!data.props.itemId) {
        newData.props.itemId = data.props.itemId;
      }

      await updateConfigureWorkflow(newData, false, getItemStatusId(data));
    },
    [data, empCd, updateConfigureWorkflow]
  );

  const handleLightUpdateWorkflowSave = React.useCallback(
    async ({
      requestCreativeHelp,
      configureWorkflowType,
      configureWorkflowSkipIds
    }: WorkflowSavePayload) => {
      const newData: Partial<Metadata> = {
        id: data.id,
        labels: data.labels,
        props: {
          refId: data.props.refId,
          requestCreativeHelp,
          configureWorkflowByEmpCd: empCd,
          configureLightUpdateWorkflowType: configureWorkflowType,
          configureLightUpdateWorkflowSkipIds: configureWorkflowSkipIds
        }
      };
      if (!!data.props.itemId) {
        newData.props.itemId = data.props.itemId;
      }
      await updateConfigureWorkflow(newData, true, getItemStatusId(data));
    },
    [data, empCd, updateConfigureWorkflow]
  );

  const displayValues = React.useMemo(
    () =>
      (Array.isArray(value) ? value : [value])
        .map(v => enumValues[v] || v)
        .filter(Boolean)
        .join(', '),
    [value, enumValues]
  );

  const lightUpdateDisplayValues = React.useMemo(
    () =>
      (Array.isArray(lightUpdateValue) ? lightUpdateValue : [lightUpdateValue])
        .map(v => lightUpdateEnumValues[v] || v)
        .filter(Boolean)
        .join(', '),
    [lightUpdateValue, lightUpdateEnumValues]
  );

  // Check if we should show the Edit link for regular workflow
  const shouldShowEdit = React.useMemo(() => {
    if (disabled || !data.props.refId) return false;

    // Show for original status ranges only
    return (
      [
        ItemStatus.Not_Pursued,
        ItemStatus.Idea,
        ItemStatus.Concept_Review
      ].includes(itemStatusId) ||
      (itemStatusId >= ItemStatus.Concept &&
        itemStatusId <= ItemStatus.WaitingforEditing)
    );
  }, [itemStatusId, disabled, data.props.refId]);

  // Check if we should show the light update field
  const shouldShowLightUpdateField = React.useMemo(() => {
    if (!lightUpdateField) return false;

    // Show if in light update status (3000-3400) OR value has been set
    const isLightUpdateStatus =
      itemStatusId >= ItemStatus.LightUpdateDraft &&
      itemStatusId <= ItemStatus.LightUpdatePrepPublication;
    const hasLightUpdateValue = !!lightUpdateDisplayValues;

    return isLightUpdateStatus || hasLightUpdateValue;
  }, [lightUpdateField, itemStatusId, lightUpdateDisplayValues]);

  // Check if we should show edit for light update field
  const shouldShowLightUpdateEdit = React.useMemo(() => {
    if (disabled || !data.props.refId) return false;

    if (
      [
        ItemStatus.Published,
        ItemStatus.PendingRepublish,
        ItemStatus.Complete
      ].includes(itemStatusId)
    )
      return false;

    // Show if in light update status (3000-3400) OR value has been set
    const isLightUpdateStatus =
      itemStatusId >= ItemStatus.LightUpdateDraft &&
      itemStatusId <= ItemStatus.LightUpdatePrepPublication;
    const hasLightUpdateValue = !!lightUpdateDisplayValues;

    return isLightUpdateStatus || hasLightUpdateValue;
  }, [itemStatusId, disabled, data.props.refId, lightUpdateDisplayValues]);

  return (
    <>
      <FieldGroup
        {...props}
        disabled={false}
        value={displayValues}
        componentRight={
          shouldShowEdit ? (
            <div className="fgl-link" onClick={onShowModal}>
              Edit
            </div>
          ) : null
        }
      >
        {displayValues || '--'}
      </FieldGroup>

      {shouldShowLightUpdateField && lightUpdateField && (
        <FieldGroup
          {...props}
          field={lightUpdateField}
          disabled={false}
          value={lightUpdateDisplayValues}
          componentRight={
            shouldShowLightUpdateEdit ? (
              <div className="fgl-link" onClick={onShowLightUpdateModal}>
                Edit
              </div>
            ) : null
          }
        >
          {lightUpdateDisplayValues || '--'}
        </FieldGroup>
      )}

      {showConfigureWorkflowModal && initialWorkflowState && (
        <Modal
          header={
            isLightUpdateModal
              ? 'Configure Light Update Workflow'
              : 'Configure Workflow'
          }
          id="configureWorkflowModal"
          onClose={closeModal}
          footer={null}
        >
          <ConfigureWorkflowContent
            isLightUpdate={isLightUpdateModal}
            data={data}
            initialWorkflowState={initialWorkflowState}
            onSave={
              isLightUpdateModal
                ? handleLightUpdateWorkflowSave
                : handleMainWorkflowSave
            }
            onClose={closeModal}
          />
        </Modal>
      )}
    </>
  );
};
